import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { Observable } from "rxjs";
import { DecisionActionRequest, DecisionState, NextBestDecision } from "../../../models/home";
import {
  DecisionActionPresentation,
  HomeDecisionActionUxService
} from "../../../services/home-ia/home-decision-action-ux.service";
import {
  DecisionConfidenceDisplay,
  DecisionPriorityDisplay,
  DecisionRiskDisplay,
  HomeDecisionDisplayUxService
} from "../../../services/home-ia/home-decision-display-ux.service";
import { LanguageService } from "../../../services/language/language.service";
import { UxError } from "../../../models/ux-error.model";
import { formatDecisionTitle } from "../../../utils/decision-title-format.util";
import { PlanFeatureService } from "../../../features/plans/services/plan-feature.service";
import {
  CHANGE_DETECTION_FEATURE_CODE,
  KNOWLEDGE_ALERTS_FEATURE_CODE,
  KNOWLEDGE_HUB_FEATURE_CODE
} from "../../../features/plans/models/entitlement.model";

export interface NextBestDecisionActionRequestEvent {
  decisionId: string;
  actionKey: string;
  request?: DecisionActionRequest;
}

/** Acceso de navegación (deep-link) ofrecido en el estado all-clear del hero. No ejecuta acciones. */
export interface AllClearNavLink {
  readonly labelKey: string;
  readonly route: string;
  readonly icon: string;
  readonly available$: Observable<boolean>;
}

@Component({
  selector: "app-home-next-best-decision",
  templateUrl: "./home-next-best-decision.component.html",
  styleUrls: [
    "./home-next-best-decision.component.summary-and-primary-action.scss",
    "./home-next-best-decision.component.secondary-actions-and-responsive.scss",
    "./home-next-best-decision.component.loading-and-all-clear.scss"
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomeNextBestDecisionComponent {
  @Input() nextDecision: NextBestDecision | null = null;
  @Input() loading = false;
  @Input() error: UxError | null = null;
  @Input() selectedDecisionId: string | null = null;
  @Input() actionLoading = false;
  @Input() actionMessage: string | null = null;
  @Input() actionError: UxError | null = null;
  @Output() retryRequest = new EventEmitter<void>();
  @Output() selectDecision = new EventEmitter<string>();
  @Output() executeActionRequest = new EventEmitter<NextBestDecisionActionRequestEvent>();
  readonly formatDecisionTitle = formatDecisionTitle;

  /**
   * Accesos de navegación del estado all-clear. Son deep-links de solo navegación (no ejecutan
   * ninguna acción). Cada uno se muestra únicamente si su feature está disponible en el plan
   * efectivo, reutilizando el gating de `PlanFeatureService` (mismo patrón que el widget de alertas).
   */
  readonly allClearLinks: readonly AllClearNavLink[];

  constructor(
    public readonly lang: LanguageService,
    private readonly actionUx: HomeDecisionActionUxService,
    private readonly decisionDisplayUx: HomeDecisionDisplayUxService,
    private readonly planFeatures: PlanFeatureService
  ) {
    this.allClearLinks = [
      {
        labelKey: "home.nextBestDecision.allClear.reviewExpirations",
        route: "/knowledge",
        icon: "fa-calendar-check",
        available$: this.planFeatures.hasFeature(KNOWLEDGE_HUB_FEATURE_CODE)
      },
      {
        labelKey: "home.nextBestDecision.allClear.viewChanges",
        route: "/changes",
        icon: "fa-clock-rotate-left",
        available$: this.planFeatures.hasFeature(CHANGE_DETECTION_FEATURE_CODE)
      },
      {
        labelKey: "home.nextBestDecision.allClear.viewAlerts",
        route: "/alerts",
        icon: "fa-bell",
        available$: this.planFeatures.hasFeature(KNOWLEDGE_ALERTS_FEATURE_CODE)
      }
    ];
  }

  get isSelected(): boolean {
    return this.selectedDecisionId === this.nextDecision?.decision?.decisionId;
  }

  get stateLabel(): string {
    return this.lang.translate(`home.decisionStates.${this.nextDecision?.decision?.state ?? "waiting"}`);
  }

  get actionPresentation(): DecisionActionPresentation {
    return this.actionUx.getHeroActionPresentation(this.nextDecision);
  }

  get actionDisabledReason(): string | null {
    return this.actionPresentation.disabledReason;
  }

  get confidenceLabel(): string {
    return this.confidenceDisplay.percentageLabel;
  }

  get priorityDisplay(): DecisionPriorityDisplay {
    const decision = this.nextDecision?.decision;
    return this.decisionDisplayUx.getPriorityDisplay(
      decision?.priority ?? { score: 0 },
      decision?.state ?? "waiting",
      decision?.dueAt
    );
  }

  get riskDisplay(): DecisionRiskDisplay {
    const decision = this.nextDecision?.decision;
    return this.decisionDisplayUx.getRiskDisplay(
      decision?.risk ?? {},
      decision?.state ?? "waiting"
    );
  }

  get confidenceDisplay(): DecisionConfidenceDisplay {
    return this.decisionDisplayUx.getConfidenceDisplay(
      this.nextDecision?.decision?.confidence ?? { overall: 0, warnings: [] }
    );
  }

  get showPrimaryAction(): boolean {
    return !!this.actionPresentation.primaryLabel && this.actionPresentation.primaryMode !== "none";
  }

  get isPrimaryActionDisabled(): boolean {
    if (this.actionPresentation.primaryMode === "disabled") {
      return true;
    }

    return this.actionPresentation.primaryMode === "execute" && this.actionLoading;
  }

  get showSecondaryAction(): boolean {
    return this.actionPresentation.showSecondaryDetail;
  }

  onRetry(): void {
    this.retryRequest.emit();
  }

  onSelectDecision(): void {
    const decisionId = this.nextDecision?.decision?.decisionId;
    if (!decisionId) {
      return;
    }

    this.selectDecision.emit(decisionId);
  }

  onExecutePrimaryAction(): void {
    if (this.actionPresentation.primaryMode === "inspect") {
      this.onSelectDecision();
      return;
    }

    if (this.actionPresentation.primaryMode !== "execute") {
      return;
    }

    const decisionId = this.nextDecision?.decision?.decisionId;
    const action = this.actionPresentation.action;
    const actionKey = action?.actionKey;
    const request = this.actionUx.buildRequest(action ?? null);

    if (!decisionId || !actionKey || !request || this.actionLoading) {
      return;
    }

    this.executeActionRequest.emit({ decisionId, actionKey, request });
  }

  getPriorityTone(score: number): "high" | "medium" | "low" {
    if (score >= 80) {
      return "high";
    }

    if (score >= 50) {
      return "medium";
    }

    return "low";
  }

  getStateTone(state: DecisionState): "critical" | "active" | "stable" {
    if (state === "act_now") {
      return "critical";
    }

    if (state === "act_soon" || state === "waiting") {
      return "active";
    }

    return "stable";
  }
}
